import React, { useState, useEffect, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import {
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import { fetchData, postData } from "../../services/apiServices";
import debounce from "lodash/debounce";

const WithdrawData = () => {
  const [modal, setModal] = useState(false);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("all");
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    username: "",
  });
  const [viewModal, setViewModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const columns = [
    {
      name: "User",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <img
            src={row.user_id?.profile_image || "/default-user.png"}
            alt=""
            width="32"
            height="32"
            style={{ borderRadius: "50%" }}
          />
          <div>
            <div>{row.user_id?.username || "N/A"}</div>
            <small className="text-muted">{row.user_id?.email || "-"}</small>
          </div>
        </div>
      ),
    },
    {
      name: "Amount",
      selector: (row) => row.amount ?? "-",
      sortable: true,
    },
    {
      name: "Type",
      selector: (row) => row.deposit_type || "-",
    },
    {
      name: "Currency",
      selector: (row) => row.currency?.toUpperCase() || "-",
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge ${
            row.status === "approved"
              ? "bg-success"
              : row.status === "pending"
                ? "bg-warning"
                : "bg-danger"
          }`}>
          {row.status || "N/A"}
        </span>
      ),
    },
    {
      name: "Transaction ID",
      selector: (row) => row.Transaction_ID || "-",
    },
    {
      name: "Date",
      selector: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleString() : "-",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Button
          size="sm"
          color="info"
          onClick={() => {
            setSelectedDeposit(row);
            setViewModal(true);
          }}>
          View
        </Button>
      ),
    },
  ];

  const getData = async (page, searchQuery = "") => {
    setPending(true);
    try {
      const params = {
        page,
        limit: perPage,
        search: searchQuery,
      };

      const response = await fetchData(
        "api/v2/admin/auth/transactions/withdrawals",
        params,
      );

      if (response?.status) {
        setRows(response.data);
        setTotalRows(response.pagination.totalDeposits);
      } else {
        setRows([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Failed to fetch deposits:", error);
      setRows([]);
      setTotalRows(0);
    } finally {
      setPending(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      getData(1, searchQuery);
      setCurrentPage(1);
    }, 300), // Reduced debounce time for better responsiveness
    [perPage],
  );

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    debouncedSearch(searchValue);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getData(page, search);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPending(true);
    setPerPage(newPerPage);

    try {
      const params = { page, limit: newPerPage, search };
      const response = await fetchData(
        "api/v2/admin/auth/transactions/withdrawals",
        params,
      );

      setRows(response.data);
      setTotalRows(response.pagination.totalDeposits);
    } catch (error) {
      console.error(error);
    } finally {
      setPending(false);
    }
  };

  const handleUserTypeChange = (e) => {
    setUserType(e.target.value);
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setNewUser({ email: "", password: "", username: "" });
    }
  };

  useEffect(() => {
    getData(1, "");
  }, []);
  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await postData("api/admin/approveDeposit", {
        depositId: selectedDeposit._id,
      });

      setViewModal(false);
      getData(currentPage, search); // refresh table
    } catch (err) {
      console.error("Approve error", err);
    } finally {
      setActionLoading(false);
    }
  };
  const handleReject = async () => {
    if (!rejectReason.trim()) return alert("Enter rejection reason");

    try {
      setActionLoading(true);
      await postData("api/admin/rejectDeposit", {
        depositId: selectedDeposit._id,
        reason: rejectReason,
      });

      setViewModal(false);
      setRejectReason("");
      getData(currentPage, search);
    } catch (err) {
      console.error("Reject error", err);
    } finally {
      setActionLoading(false);
    }
  };
  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-3 align-items-center">
            <Input
              type="search"
              placeholder="Search by users,status, txn id..."
              value={search}
              onChange={handleSearch}
              style={{ width: "250px" }}
            />
            {/* <Input
              type="select"
              value={userType}
              onChange={handleUserTypeChange}
              style={{ width: "150px" }}
            >
              <option value="all">All Users</option>
              <option value="internal">Internal</option>
              <option value="external">External</option>
            </Input> */}
          </div>
          {/* <Button color="primary" onClick={toggleModal}>
            Create User
          </Button> */}
        </div>

        <DataTable
          columns={columns}
          data={rows}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={pending}
          responsive
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 30, 50, 100]}
          noDataComponent={
            <div className="py-4 text-center text-muted">
              <b>No transactions found</b>
            </div>
          }
        />

        <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
          <ModalHeader toggle={() => setViewModal(false)}>
            Deposit Details
          </ModalHeader>

          <ModalBody>
            {selectedDeposit && (
              <div className="container-fluid">
                <Row className="mb-2">
                  <Col md={6}>
                    <b>User:</b> {selectedDeposit.user_id?.username}
                  </Col>
                  <Col md={6}>
                    <b>Email:</b> {selectedDeposit.user_id?.email}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <b>Amount:</b> ₹{selectedDeposit.amount}
                  </Col>
                  <Col md={6}>
                    <b>Status:</b> {selectedDeposit.status}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <b>Type:</b> {selectedDeposit.deposit_type}
                  </Col>
                  <Col md={6}>
                    <b>Currency:</b> {selectedDeposit.currency}
                  </Col>
                </Row>

                {selectedDeposit.Transaction_ID && (
                  <Row className="mb-2">
                    <Col>
                      <b>Transaction ID:</b> {selectedDeposit.Transaction_ID}
                    </Col>
                  </Row>
                )}

                {selectedDeposit.Bank_Name && (
                  <>
                    <Row className="mb-2">
                      <Col md={6}>
                        <b>Bank:</b> {selectedDeposit.Bank_Name}
                      </Col>
                      <Col md={6}>
                        <b>Account No:</b> {selectedDeposit.Account_NO}
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col md={6}>
                        <b>IFSC:</b> {selectedDeposit.IFSC_code}
                      </Col>
                    </Row>
                  </>
                )}

                {selectedDeposit.transactionDocument && (
                  <div className="mt-3 text-center">
                    <b>Payment Proof:</b>
                    <br />
                    <img
                      src={selectedDeposit.transactionDocument}
                      alt="proof"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        marginTop: "10px",
                      }}
                    />
                  </div>
                )}

                {selectedDeposit.status === "pending" && (
                  <div className="mt-3">
                    <Label>Rejection Reason</Label>
                    <Input
                      type="textarea"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
          </ModalBody>

          {selectedDeposit?.status === "pending" && (
            <ModalFooter>
              <Button
                color="success"
                onClick={handleApprove}
                disabled={actionLoading}>
                Approve
              </Button>
              <Button
                color="danger"
                onClick={handleReject}
                disabled={actionLoading}>
                Reject
              </Button>
              <Button color="secondary" onClick={() => setViewModal(false)}>
                Close
              </Button>
            </ModalFooter>
          )}
        </Modal>
      </CardBody>
    </Card>
  );
};

export { WithdrawData };
